import { __extends } from "tslib";
import { Vector2 } from "../Maths/math.vector.js";
import { Texture } from "../Materials/Textures/texture.js";
import { PostProcessRenderEffect } from "../PostProcesses/RenderPipeline/postProcessRenderEffect.js";
import { CircleOfConfusionPostProcess } from "./circleOfConfusionPostProcess.js";
import { DepthOfFieldBlurPostProcess } from "./depthOfFieldBlurPostProcess.js";
import { DepthOfFieldMergePostProcess } from "./depthOfFieldMergePostProcess.js";
/**
 * Specifies the level of max blur that should be applied when using the depth of field effect
 */
export var DepthOfFieldEffectBlurLevel;
(function (DepthOfFieldEffectBlurLevel) {
    /**
     * Subtle blur
     */
    DepthOfFieldEffectBlurLevel[DepthOfFieldEffectBlurLevel["Low"] = 0] = "Low";
    /**
     * Medium blur
     */
    DepthOfFieldEffectBlurLevel[DepthOfFieldEffectBlurLevel["Medium"] = 1] = "Medium";
    /**
     * Large blur
     */
    DepthOfFieldEffectBlurLevel[DepthOfFieldEffectBlurLevel["High"] = 2] = "High";
})(DepthOfFieldEffectBlurLevel || (DepthOfFieldEffectBlurLevel = {}));
/**
 * The depth of field effect applies a blur to objects that are closer or further from where the camera is focusing.
 */
var DepthOfFieldEffect = /** @class */ (function (_super) {
    __extends(DepthOfFieldEffect, _super);
    /**
     * Creates a new instance DepthOfFieldEffect
     * @param scene The scene the effect belongs to.
     * @param depthTexture The depth texture of the scene to compute the circle of confusion.This must be set in order for this to function but may be set after initialization if needed.
     * @param blurLevel
     * @param pipelineTextureType The type of texture to be used when performing the post processing.
     * @param blockCompilation If compilation of the shader should not be done in the constructor. The updateEffect method can be used to compile the shader at a later time. (default: false)
     */
    function DepthOfFieldEffect(scene, depthTexture, blurLevel, pipelineTextureType, blockCompilation) {
        if (blurLevel === void 0) { blurLevel = DepthOfFieldEffectBlurLevel.Low; }
        if (pipelineTextureType === void 0) { pipelineTextureType = 0; }
        if (blockCompilation === void 0) { blockCompilation = false; }
        var _this = _super.call(this, scene.getEngine(), "depth of field", function () {
            return _this._effects;
        }, true) || this;
        /**
         * @hidden Internal post processes in depth of field effect
         */
        _this._effects = [];
        // Circle of confusion value for each pixel is used to determine how much to blur that pixel
        _this._circleOfConfusion = new CircleOfConfusionPostProcess("circleOfConfusion", depthTexture, 1, null, Texture.BILINEAR_SAMPLINGMODE, scene.getEngine(), false, pipelineTextureType, blockCompilation);
        // Create a pyramid of blurred images (eg. fullSize 1/4 blur, half size 1/2 blur, quarter size 3/4 blur, eith size 4/4 blur)
        // Blur the image but do not blur on sharp far to near distance changes to avoid bleeding artifacts
        // See section 2.6.2 http://fileadmin.cs.lth.se/cs/education/edan35/lectures/12dof.pdf
        _this._depthOfFieldBlurY = [];
        _this._depthOfFieldBlurX = [];
        var blurCount = 1;
        var kernelSize = 15;
        switch (blurLevel) {
            case DepthOfFieldEffectBlurLevel.High: {
                blurCount = 3;
                kernelSize = 51;
                break;
            }
            case DepthOfFieldEffectBlurLevel.Medium: {
                blurCount = 2;
                kernelSize = 31;
                break;
            }
            default: {
                kernelSize = 15;
                blurCount = 1;
                break;
            }
        }
        var adjustedKernelSize = kernelSize / Math.pow(2, blurCount - 1);
        var ratio = 1.0;
        for (var i = 0; i < blurCount; i++) {
            var blurY = new DepthOfFieldBlurPostProcess("vertical blur", scene, new Vector2(0, 1.0), adjustedKernelSize, ratio, null, _this._circleOfConfusion, i == 0 ? _this._circleOfConfusion : null, Texture.BILINEAR_SAMPLINGMODE, scene.getEngine(), false, pipelineTextureType, blockCompilation);
            blurY.autoClear = false;
            ratio = 0.75 / Math.pow(2, i);
            var blurX = new DepthOfFieldBlurPostProcess("horizontal blur", scene, new Vector2(1.0, 0), adjustedKernelSize, ratio, null, _this._circleOfConfusion, null, Texture.BILINEAR_SAMPLINGMODE, scene.getEngine(), false, pipelineTextureType, blockCompilation);
            blurX.autoClear = false;
            _this._depthOfFieldBlurY.push(blurY);
            _this._depthOfFieldBlurX.push(blurX);
        }
        // Set all post processes on the effect.
        _this._effects = [_this._circleOfConfusion];
        for (var i = 0; i < _this._depthOfFieldBlurX.length; i++) {
            _this._effects.push(_this._depthOfFieldBlurY[i]);
            _this._effects.push(_this._depthOfFieldBlurX[i]);
        }
        // Merge blurred images with original image based on circleOfConfusion
        _this._dofMerge = new DepthOfFieldMergePostProcess("dofMerge", _this._circleOfConfusion, _this._circleOfConfusion, _this._depthOfFieldBlurX, ratio, null, Texture.BILINEAR_SAMPLINGMODE, scene.getEngine(), false, pipelineTextureType, blockCompilation);
        _this._dofMerge.autoClear = false;
        _this._effects.push(_this._dofMerge);
        return _this;
    }
    Object.defineProperty(DepthOfFieldEffect.prototype, "focalLength", {
        get: function () {
            return this._circleOfConfusion.focalLength;
        },
        /**
         * The focal the length of the camera used in the effect in scene units/1000 (eg. millimeter)
         */
        set: function (value) {
            this._circleOfConfusion.focalLength = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DepthOfFieldEffect.prototype, "fStop", {
        get: function () {
            return this._circleOfConfusion.fStop;
        },
        /**
         * F-Stop of the effect's camera. The diameter of the resulting aperture can be computed by lensSize/fStop. (default: 1.4)
         */
        set: function (value) {
            this._circleOfConfusion.fStop = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DepthOfFieldEffect.prototype, "focusDistance", {
        get: function () {
            return this._circleOfConfusion.focusDistance;
        },
        /**
         * Distance away from the camera to focus on in scene units/1000 (eg. millimeter). (default: 2000)
         */
        set: function (value) {
            this._circleOfConfusion.focusDistance = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DepthOfFieldEffect.prototype, "lensSize", {
        get: function () {
            return this._circleOfConfusion.lensSize;
        },
        /**
         * Max lens size in scene units/1000 (eg. millimeter). Standard cameras are 50mm. (default: 50) The diameter of the resulting aperture can be computed by lensSize/fStop.
         */
        set: function (value) {
            this._circleOfConfusion.lensSize = value;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Get the current class name of the current effect
     * @returns "DepthOfFieldEffect"
     */
    DepthOfFieldEffect.prototype.getClassName = function () {
        return "DepthOfFieldEffect";
    };
    Object.defineProperty(DepthOfFieldEffect.prototype, "depthTexture", {
        /**
         * Depth texture to be used to compute the circle of confusion. This must be set here or in the constructor in order for the post process to function.
         */
        set: function (value) {
            this._circleOfConfusion.depthTexture = value;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Disposes each of the internal effects for a given camera.
     * @param camera The camera to dispose the effect on.
     */
    DepthOfFieldEffect.prototype.disposeEffects = function (camera) {
        for (var effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
            this._effects[effectIndex].dispose(camera);
        }
    };
    /**
     * @hidden Internal
     */
    DepthOfFieldEffect.prototype._updateEffects = function () {
        for (var effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
            this._effects[effectIndex].updateEffect();
        }
    };
    /**
     * Internal
     * @returns if all the contained post processes are ready.
     * @hidden
     */
    DepthOfFieldEffect.prototype._isReady = function () {
        for (var effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
            if (!this._effects[effectIndex].isReady()) {
                return false;
            }
        }
        return true;
    };
    return DepthOfFieldEffect;
}(PostProcessRenderEffect));
export { DepthOfFieldEffect };
//# sourceMappingURL=depthOfFieldEffect.js.map